<script setup lang="ts">
import { useToast } from '@/composables/useToast';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/vue/24/outline';

const { toasts, removeToast } = useToast();

const getIcon = (type: string) => {
  switch (type) {
    case 'success': return CheckCircleIcon;
    case 'error': return XCircleIcon;
    case 'warning': return ExclamationTriangleIcon;
    default: return InformationCircleIcon;
  }
};

const getColorClasses = (type: string) => {
  switch (type) {
    case 'success': return 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800 text-green-800 dark:text-green-200';
    case 'error': return 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200';
    case 'warning': return 'bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200';
    default: return 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200';
  }
};

const getIconClasses = (type: string) => {
  switch (type) {
    case 'success': return 'text-green-500 dark:text-green-400';
    case 'error': return 'text-red-500 dark:text-red-400';
    case 'warning': return 'text-amber-500 dark:text-amber-400';
    default: return 'text-blue-500 dark:text-blue-400';
  }
};
</script>

<template>
  <Teleport to="body">
    <div class="fixed top-4 right-4 z-[100] flex flex-col gap-2 max-w-sm">
      <TransitionGroup
        enter-active-class="transition-all duration-300 ease-out"
        enter-from-class="opacity-0 translate-x-8"
        enter-to-class="opacity-100 translate-x-0"
        leave-active-class="transition-all duration-200 ease-in"
        leave-from-class="opacity-100 translate-x-0"
        leave-to-class="opacity-0 translate-x-8"
      >
        <div
          v-for="toast in toasts"
          :key="toast.id"
          :class="[
            'flex items-start gap-3 px-4 py-3 rounded-lg border shadow-lg backdrop-blur-sm',
            getColorClasses(toast.type)
          ]"
        >
          <component
            :is="getIcon(toast.type)"
            :class="['w-5 h-5 flex-shrink-0 mt-0.5', getIconClasses(toast.type)]"
          />
          <p class="flex-1 text-sm font-medium">{{ toast.message }}</p>
          <button
            @click="removeToast(toast.id)"
            class="flex-shrink-0 p-0.5 rounded hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
          >
            <XMarkIcon class="w-4 h-4" />
          </button>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>
